// Multi-brand product catalog for Skytion Enterprises
// Updated to use consolidated product data slugs

export interface ProductSpecifications {
  dimension?: string;
  material: string;
  weight?: string;
  ply: string;
  gsm?: string;
  sheetSize?: string;
  sheetsPerPack?: string;
  packsPerCarton?: string;
  application: string[];
}

export interface Product {
  id: string;
  name: string;
  brand: 'SKYSUN' | 'SKYTION';
  image: string;
  gallery?: string[];
  description: string;
  specs: ProductSpecifications;
  features: string[];
  useCases: string[];
  category: string;
  link: string;
  seoTitle: string;
  seoDescription: string;
}

export interface Brand {
  id: string;
  name: string;
  displayName: string;
  logo?: string;
  tagline: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  positioning: string;
  targetMarket: string[];
  certifications?: string[];
}

// Brand Definitions
export const brands: Record<string, Brand> = {
  SKYSUN: {
    id: 'skysun',
    name: 'SKYSUN',
    displayName: 'Sky Sun',
    tagline: 'Premium Quality, Trusted Comfort',
    description: 'Sky Sun is our flagship brand delivering premium tissue products made from virgin and recycled pulp. Trusted by households and businesses worldwide for superior quality and comfort.',
    colors: {
      primary: '#FF6B35',
      secondary: '#F7931E',
      accent: '#FDB44B'
    },
    positioning: 'Premium Brand',
    targetMarket: ['Retail', 'Hospitality', 'Commercial', 'Export'],
    certifications: ['ISO 9001:2015', 'FSC Certified', 'Quality Assured']
  },
  SKYTION: {
    id: 'skytion',
    name: 'SKYTION',
    displayName: 'Skytion',
    tagline: 'Affordable Excellence',
    description: 'Skytion provides economical tissue solutions without compromising on essential quality. Ideal for budget-conscious businesses and large-scale operations.',
    colors: {
      primary: '#10b981',
      secondary: '#059669',
      accent: '#34d399'
    },
    positioning: 'Economy Brand',
    targetMarket: ['Economy', 'Bulk Distribution', 'Large Scale', 'Cost-Effective'],
    certifications: ['Quality Tested']
  }
};

// Product Catalog - Updated with correct image paths and slugs
export const productsCatalog: Product[] = [
  // SKYSUN Products
  {
    id: 'skysun-toilet-roll-6in1',
    name: '6 in 1 Toilet Roll',
    brand: 'SKYSUN',
    image: '/assets/skytion/products/toilet-paper.png',
    gallery: [
      '/assets/skytion/products/toilet-paper.png',
    ],
    description: 'Premium 2-ply soft toilet rolls in convenient 6-pack. Made from virgin pulp for superior softness and strength. Perfect for households and hospitality.',
    specs: {
      dimension: '10cm x 10cm (4" x 4")',
      material: 'Virgin Pulp',
      weight: '480 gram per pack',
      ply: '2',
      gsm: '13-14 GSM',
      application: ['Household', 'Hotels', 'Restaurants', 'Commercial Washrooms']
    },
    features: [
      'Ultra-soft 2-ply construction',
      'Made from 100% virgin pulp',
      '6 rolls per pack for convenience',
      'Superior absorbency and strength',
      'Gentle on skin',
      'Long-lasting rolls',
      'Hygienic packaging',
      'Eco-friendly manufacturing'
    ],
    useCases: [
      'Premium hotels and resorts',
      'High-end residential complexes',
      'Restaurants and cafes',
      'Corporate offices',
      'Healthcare facilities',
      'Retail distribution'
    ],
    category: 'Bathroom',
    link: '/products/skysun-2ply-toilet-roll',
    seoTitle: 'Sky Sun 6-in-1 Toilet Roll - Premium 2 Ply Tissue',
    seoDescription: 'Premium Sky Sun toilet rolls made from virgin pulp. 6 rolls per pack, 2-ply softness, 480g.'
  },
  {
    id: 'skysun-tissue-paper-10x10',
    name: 'Tissue Paper 10x10',
    brand: 'SKYSUN',
    image: '/assets/skytion/products/2-ply-tissue-paper.png',
    gallery: [
      '/assets/skytion/products/2-ply-tissue-paper.png',
    ],
    description: 'Premium 2-ply dining tissue papers (25cm x 25cm). Perfect for dining tables, restaurants, and hospitality. Superior quality and presentation.',
    specs: {
      dimension: '25cm x 25cm (10" x 10")',
      material: 'Virgin Pulp + Recycled Fiber',
      ply: '2',
      gsm: '13-14 GSM',
      sheetsPerPack: '100 sheets',
      application: ['Dining', 'Restaurants', 'Hospitality', 'Events']
    },
    features: [
      'Premium 2-ply construction',
      'Perfect square size 10x10 inches',
      'Soft and absorbent',
      'Elegant presentation',
      '100 sheets per pack',
      'Food-safe materials',
      'Suitable for dining applications',
      'Consistent quality'
    ],
    useCases: [
      'Fine dining restaurants',
      'Hotel dining services',
      'Catering and events',
      'Corporate dining halls',
      'Premium cafes',
      'Banquet services'
    ],
    category: 'Dining',
    link: '/products/2ply-premium-napkin',
    seoTitle: 'Sky Sun Tissue Paper 10x10 - Premium Dining Napkins',
    seoDescription: 'Sky Sun premium dining tissue papers. 2-ply, 25cm x 25cm, 100 sheets per pack.'
  },
  {
    id: 'skysun-kitchen-towel',
    name: 'Kitchen Towel 2 Ply',
    brand: 'SKYSUN',
    image: '/assets/skytion/products/kitchen-towel-png.png',
    description: 'Heavy-duty kitchen towels with superior absorbency. 2-ply construction for strength and durability. Ideal for kitchen cleaning and food preparation areas.',
    specs: {
      dimension: '10cm x 20cm (4" x 8")',
      material: 'Virgin Pulp',
      weight: '240 gram',
      ply: '2',
      application: ['Kitchen', 'Food Service', 'Cleaning', 'Commercial Kitchens']
    },
    features: [
      'Extra absorbent 2-ply',
      'Strong when wet',
      'Suitable for kitchen tasks',
      'Food-safe materials',
      'Tear-resistant',
      'Quick-drying formula',
      'Multi-purpose use',
      'Value pack sizing'
    ],
    useCases: [
      'Home kitchens',
      'Restaurant kitchens',
      'Catering services',
      'Food processing units',
      'Commercial cleaning',
      'Hospitality industry'
    ],
    category: 'Kitchen',
    link: '/products/kitchen-towel',
    seoTitle: 'Sky Sun Kitchen Towel - 2 Ply Absorbent Paper Towels',
    seoDescription: 'Sky Sun kitchen towels with superior absorbency. 2-ply, 240g, perfect for kitchens.'
  },

  // SKYTION Products
  {
    id: 'skytion-toilet-roll',
    name: 'Economy Toilet Roll',
    brand: 'SKYTION',
    image: '/assets/skytion/products/toilet-paper.png',
    description: 'Budget-friendly toilet rolls for large-scale operations. Single-ply economical solution maintaining essential quality standards.',
    specs: {
      dimension: '10cm x 10cm',
      material: 'Recycled Fiber',
      ply: '1',
      weight: '320 gram per pack',
      application: ['Bulk Distribution', 'Large Facilities', 'Budget Operations']
    },
    features: [
      'Most economical option',
      'Single-ply cost-effective',
      'Suitable for high volume',
      'Bulk packaging',
      'Essential quality maintained',
      'Large-scale distribution',
      'Value pricing',
      'Reliable supply chain'
    ],
    useCases: [
      'Large institutional facilities',
      'Budget hotels',
      'Labor camps',
      'Government facilities',
      'Bulk distributors',
      'Cost-sensitive operations'
    ],
    category: 'Economy',
    link: '/products/jumbo-toilet-roll',
    seoTitle: 'Skytion Economy Toilet Roll - Budget Tissue Paper',
    seoDescription: 'Skytion economical toilet rolls for bulk operations. Single-ply, 320g.'
  },
  {
    id: 'skytion-napkin',
    name: 'Economy Napkin',
    brand: 'SKYTION',
    image: '/assets/skytion/products/1-ply-single-tissue.png',
    description: 'Basic quality napkins for large-scale food service operations. Maximum value for budget-focused businesses.',
    specs: {
      dimension: '25cm x 25cm',
      material: 'Recycled Fiber',
      ply: '1',
      sheetsPerPack: '100 sheets',
      application: ['Bulk Food Service', 'Canteens', 'Large Events']
    },
    features: [
      'Maximum value pricing',
      'Standard napkin size',
      'High volume packs',
      'Suitable for bulk use',
      'Economical option',
      'Basic quality assured',
      'Large pack sizes',
      'Distribution-ready'
    ],
    useCases: [
      'Large cafeterias',
      'Mass catering',
      'Event management',
      'Wholesale distribution',
      'Budget food service',
      'Community kitchens'
    ],
    category: 'Economy',
    link: '/products/single-ply-tissue',
    seoTitle: 'Skytion Economy Napkin - Bulk Food Service Tissue',
    seoDescription: 'Skytion economical napkins for large-scale food service. Single-ply, 100 sheets.'
  },

  // Additional products mapped to brands
  {
    id: 'skysun-mini-tissue',
    name: 'Mini Tissue Pack',
    brand: 'SKYSUN',
    image: '/assets/skytion/products/skytion-mini-tissue-papers.png',
    description: 'Convenient pocket-sized tissue packs. Perfect for travel, retail, and on-the-go use. Premium softness in compact packaging.',
    specs: {
      dimension: 'Pocket Size',
      material: 'Virgin Pulp',
      ply: '2',
      sheetsPerPack: '10 sheets',
      application: ['Retail', 'Travel', 'Personal Use', 'Promotional']
    },
    features: [
      'Convenient pocket size',
      'Premium 2-ply softness',
      'Hygienic sealed pack',
      'Perfect for travel',
      'Retail-ready packaging',
      'Customizable branding',
      'Individual use portions',
      'Portable and compact'
    ],
    useCases: [
      'Retail stores',
      'Travel and tourism',
      'Promotional giveaways',
      'Personal care kits',
      'Hotel amenities',
      'Airlines and transportation'
    ],
    category: 'Personal',
    link: '/products/mini-tissue',
    seoTitle: 'Sky Sun Mini Tissue Pack - Premium Pocket Tissues',
    seoDescription: 'Sky Sun mini tissue packs for travel and retail. 2-ply premium quality.'
  },
  {
    id: 'skysun-hrt-roll',
    name: 'HRT Jumbo Roll',
    brand: 'SKYSUN',
    image: '/assets/skytion/products/HTR-tissue-paper.jpg',
    description: 'Industrial HRT (Hard Rolled Tissue) jumbo rolls. Premium quality for commercial dispensers and high-traffic areas.',
    specs: {
      dimension: 'Jumbo Roll',
      material: 'Virgin Pulp',
      ply: '1',
      weight: 'Variable',
      application: ['Industrial', 'Commercial Dispensers', 'High Traffic Areas']
    },
    features: [
      'Large jumbo roll format',
      'Reduces change frequency',
      'Compatible with dispensers',
      'Economical for high traffic',
      'Consistent quality',
      'Long-lasting supply',
      'Professional grade',
      'Cost-effective per use'
    ],
    useCases: [
      'Industrial facilities',
      'Large commercial buildings',
      'Shopping centers',
      'Transportation hubs',
      'Sports facilities',
      'Entertainment venues'
    ],
    category: 'Industrial',
    link: '/products/hrt-roll',
    seoTitle: 'Sky Sun HRT Jumbo Roll - Industrial Tissue Paper',
    seoDescription: 'Sky Sun HRT jumbo rolls for industrial and commercial use. Premium quality.'
  }
];

// Helper functions
export const getProductsByBrand = (brandName: 'SKYSUN' | 'SKYTION'): Product[] => {
  return productsCatalog.filter(product => product.brand === brandName);
};

export const getBrandInfo = (brandName: 'SKYSUN' | 'SKYTION'): Brand => {
  return brands[brandName];
};

export const getAllBrands = (): Brand[] => {
  return Object.values(brands);
};

export const getProductById = (id: string): Product | undefined => {
  return productsCatalog.find(product => product.id === id);
};

export const getProductsByCategory = (category: string): Product[] => {
  return productsCatalog.filter(product => product.category === category);
};
